import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import logger from '@/lib/logger';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

let memoryToken = null;
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) { refreshSubscribers.push(cb); }
function onRefreshed() { refreshSubscribers.forEach(cb => cb()); refreshSubscribers = []; }

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/')) {
      originalRequest._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await axios.post(`${API}/auth/refresh`);
          isRefreshing = false;
          onRefreshed();
          return axios(originalRequest);
        } catch (refreshErr) {
          isRefreshing = false;
          logger.warn('Token refresh failed:', refreshErr.message);
          return Promise.reject(error);
        }
      }
      return new Promise(resolve => {
        subscribeTokenRefresh(() => resolve(axios(originalRequest)));
      });
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (memoryToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${memoryToken}`;
    }
    try {
      const resp = await axios.get(`${API}/auth/me`);
      setUser(resp.data);
    } catch (err) {
      logger.warn('Auth check failed:', err.message);
      memoryToken = null;
      delete axios.defaults.headers.common['Authorization'];
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const resp = await axios.post(`${API}/auth/login`, { email, password });
    if (resp.data.access_token) {
      memoryToken = resp.data.access_token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${resp.data.access_token}`;
    }
    setUser(resp.data);
    return resp.data;
  }, []);

  const register = useCallback(async (email, password, name, referralCode) => {
    const payload = { email, password, name };
    if (referralCode) payload.referral_code = referralCode;
    const resp = await axios.post(`${API}/auth/register`, payload);
    if (resp.data.access_token) {
      memoryToken = resp.data.access_token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${resp.data.access_token}`;
    }
    setUser(resp.data);
    return resp.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      logger.warn('Logout request failed:', err.message);
    }
    memoryToken = null;
    delete axios.defaults.headers.common['Authorization'];
    setUser(false);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  const value = useMemo(() => ({
    user, loading, login, register, logout, updateUser, checkAuth
  }), [user, loading, login, register, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
